import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  followUsers,
  followActivity,
  followNotificationsList,
  followDashboardStats,
  followUserTypeLabels,
  followUserTypeIcons,
  followUserTypeColors,
  getMostPopularUsers,
  getLiveUsers,
  getMutualFollowers,
  getUnreadNotifications,
  type FollowUser,
  type FollowUserType,
} from '@/mocks/rfwFollowUsers';

function UserCard({ user, variant = 'default' }: { user: FollowUser; variant?: 'default' | 'compact' | 'mini' }) {
  const [isFollowing, setIsFollowing] = useState(user.isFollowing);
  const typeInfo = followUserTypeColors[user.type];

  const toggleFollow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFollowing(!isFollowing);
  };

  if (variant === 'mini') {
    return (
      <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200">
        <div className="relative shrink-0">
          <img src={user.photoUrl} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
          {user.isLive && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-background-50 flex items-center justify-center">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{user.name}</p>
            {user.isVerified && <i className="ri-verified-badge-fill text-[10px] text-sky-500"></i>}
          </div>
          <p className="text-[10px] text-foreground-500">{user.followersCount.toLocaleString()} seguidores</p>
        </div>
        <button
          onClick={toggleFollow}
          className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
            isFollowing
              ? 'bg-rose-100 text-rose-700 border border-rose-200 hover:bg-rose-200'
              : 'bg-primary-500 text-white hover:bg-primary-600'
          }`}
        >
          {isFollowing ? 'Siguiendo' : 'Seguir'}
        </button>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to="/seguir-usuarios"
        className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/60 transition-all duration-200 group"
      >
        <div className="relative shrink-0">
          <img src={user.photoUrl} alt={user.name} className="w-11 h-11 rounded-full object-cover" />
          {user.isLive && (
            <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 border-2 border-background-50 flex items-center justify-center">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${typeInfo.badge}`}>
              {followUserTypeLabels[user.type]}
            </span>
            {user.isVerified && <i className="ri-verified-badge-fill text-[10px] text-sky-500"></i>}
          </div>
          <p className="text-xs font-semibold text-foreground-900 leading-snug line-clamp-1">{user.name}</p>
          <p className="text-[10px] text-foreground-500">@{user.username.replace('@', '')} · {user.followersCount.toLocaleString()} seguidores</p>
        </div>
      </Link>
    );
  }

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-rose-300/60 transition-all duration-300 group">
      <div className="relative h-24 overflow-hidden">
        <img src={user.coverUrl} alt={user.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        {user.isLive && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            AL AIRE
          </span>
        )}
        <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-medium border border-white/30 text-white bg-black/30 backdrop-blur-sm`}>
          {followUserTypeLabels[user.type]}
        </span>
      </div>
      <div className="px-4 pb-4 -mt-7 relative">
        <div className={`w-14 h-14 rounded-full border-[3px] border-background-50 overflow-hidden ${typeInfo.ring} ring-2`}>
          <img src={user.photoUrl} alt={user.name} className="w-full h-full object-cover" />
        </div>
        <div className="mt-2">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-sm font-bold text-foreground-900 line-clamp-1">{user.name}</p>
            {user.isVerified && <i className="ri-verified-badge-fill text-xs text-sky-500"></i>}
          </div>
          <p className="text-[11px] text-foreground-500 mb-1">@{user.username.replace('@', '')}</p>
          <p className="text-[11px] text-foreground-600 line-clamp-2 leading-relaxed mb-3">{user.bio}</p>
          <div className="flex items-center gap-1 flex-wrap mb-3">
            {user.badges.slice(0, 2).map((badge) => (
              <span key={badge} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium">{badge}</span>
            ))}
            {user.badges.length > 2 && (
              <span className="text-[9px] text-foreground-400">+{user.badges.length - 2}</span>
            )}
          </div>
          <div className="flex items-center justify-between text-[11px] mb-3">
            <div className="flex items-center gap-3">
              <span className="text-foreground-500"><strong className="text-foreground-900">{user.followersCount.toLocaleString()}</strong> seguidores</span>
              <span className="text-foreground-500"><strong className="text-foreground-900">{user.followingCount}</strong> siguiendo</span>
            </div>
            <span className="text-foreground-400 flex items-center gap-1">
              <i className="ri-map-pin-line text-[10px]"></i>
              {user.city}
            </span>
          </div>
          <button
            onClick={toggleFollow}
            className={`w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
              isFollowing
                ? 'bg-rose-100 text-rose-700 border border-rose-200 hover:bg-rose-200'
                : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
          >
            <i className={`${isFollowing ? 'ri-user-unfollow-line' : 'ri-user-add-line'} text-[11px]`}></i>
            {isFollowing ? 'Siguiendo' : 'Seguir'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityItem({ item }: { item: typeof followActivity[0] }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-rose-200/60 transition-all duration-200">
      <img src={item.userPhoto} alt={item.userName} className="w-9 h-9 rounded-full object-cover shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-xs font-semibold text-foreground-900">{item.userName}</p>
          <div className={`w-5 h-5 rounded-full ${item.iconBg} flex items-center justify-center`}>
            <i className={`${item.icon} ${item.iconColor} text-[9px]`}></i>
          </div>
        </div>
        <p className="text-[11px] text-foreground-600 leading-snug line-clamp-2">{item.description}</p>
        <p className="text-[10px] text-foreground-400 mt-1">{item.timestampRelative}</p>
      </div>
    </div>
  );
}

export default function SeguirUsuariosSection() {
  const [activeTab, setActiveTab] = useState<'descubrir' | 'seguidores' | 'actividad'>('descubrir');
  const popularUsers = getMostPopularUsers(6);
  const mutualUsers = getMutualFollowers().slice(0, 4);
  const liveUsers = getLiveUsers();
  const recentActivity = followActivity.slice(0, 5);
  const unreadNotifs = getUnreadNotifications().slice(0, 3);

  const tabs = [
    { id: 'descubrir' as const, label: 'Descubrir Gente', icon: 'ri-compass-line' },
    { id: 'seguidores' as const, label: 'Tus Seguidores', icon: 'ri-user-heart-line', badge: 12 },
    { id: 'actividad' as const, label: 'Actividad Reciente', icon: 'ri-flashlight-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            SISTEMA DE SEGUIR USUARIOS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Una Verdadera <span className="text-rose-600">Red Social</span> Radial
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Oyentes, locutores, moderadores y creadores. Seguí a las personas que hacen grande a la radio y construí tu propia comunidad. Cada conexión te acerca más a lo que amás.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Total Conexiones', value: (followDashboardStats.totalFollows / 1000).toFixed(1) + 'K', icon: 'ri-group-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Nuevas Esta Semana', value: (followDashboardStats.weeklyNewFollows / 1000).toFixed(1) + 'K', icon: 'ri-user-add-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Promedio x Usuario', value: followDashboardStats.avgFollowsPerUser.toFixed(1), icon: 'ri-user-heart-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Mutuales', value: (followDashboardStats.mutualFollows / 1000).toFixed(1) + 'K', icon: 'ri-exchange-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Usuarios Activos', value: (followDashboardStats.activeFollowUsers / 1000).toFixed(1) + 'K', icon: 'ri-user-star-line', color: 'text-sky-600 bg-sky-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'descubrir' && (
          <div className="space-y-6">
            {liveUsers.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  </div>
                  <p className="text-xs font-bold text-foreground-900">En Vivo Ahora</p>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {liveUsers.map((user) => (
                    <div key={user.id} className="flex-shrink-0 w-44">
                      <UserCard user={user} variant="mini" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">Más Populares</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {popularUsers.map((user) => (
                  <UserCard key={user.id} user={user} />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                <h4 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-exchange-line text-rose-600"></i>
                  Seguidores en Común
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {mutualUsers.map((user) => (
                    <UserCard key={user.id} user={user} variant="compact" />
                  ))}
                </div>
              </div>
              <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                <h4 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-notification-3-line text-rose-600"></i>
                  Notificaciones Recientes
                </h4>
                {unreadNotifs.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-xs text-foreground-500">¡Todo al día!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {unreadNotifs.map((notif) => (
                      <div key={notif.id} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-background-50 border border-background-200/70">
                        <div className={`w-8 h-8 rounded-full ${notif.bgColor} flex items-center justify-center shrink-0`}>
                          <i className={`${notif.icon} ${notif.color} text-sm`}></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-semibold text-foreground-900 line-clamp-1">{notif.fromName}</p>
                          <p className="text-[10px] text-foreground-500 line-clamp-1">{notif.description}</p>
                        </div>
                        <span className="text-[9px] text-foreground-400 shrink-0">{notif.timestamp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'seguidores' && (
          <div>
            <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">Te Siguen</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {popularUsers.slice(0, 8).map((user) => (
                <UserCard key={user.id} user={user} variant="compact" />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'actividad' && (
          <div>
            <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">Lo Último de tu Red</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {recentActivity.map((item) => (
                <ActivityItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          <div className="p-5 rounded-2xl bg-rose-50 border border-rose-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center mx-auto mb-2">
              <i className="ri-user-add-line text-lg text-rose-600"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">Nuevo Seguidor</p>
            <p className="text-[11px] text-foreground-500">Cuando alguien te siga, recibí una notificación al instante y conectá.</p>
          </div>
          <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center mx-auto mb-2">
              <i className="ri-medal-line text-lg text-amber-600"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">Nuevo Logro</p>
            <p className="text-[11px] text-foreground-500">Celebrá los logros de las personas que seguís. Cada hito merece reconocimiento.</p>
          </div>
          <div className="p-5 rounded-2xl bg-violet-50 border border-violet-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-violet-200 flex items-center justify-center mx-auto mb-2">
              <i className="ri-article-line text-lg text-violet-600"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">Nuevo Post</p>
            <p className="text-[11px] text-foreground-500">No te pierdas lo que publican. Enterate primero de todo lo que pasa en tu red.</p>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/seguir-usuarios"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-group-line"></i>
            Ir a la Red Social RFW
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-heart-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Debe sentirse como una verdadera red social.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Seguir a alguien no es solo un botón. Es elegir conectar con esa persona, ver lo que publica, celebrar sus logros, y construir una relación real dentro de la comunidad radial. Cada conexión es un puente entre dos personas que comparten la misma pasión: la radio. Y eso, bien hecho, se siente como una verdadera red social.
          </p>
        </div>
      </div>
    </section>
  );
}